import { Feature } from 'ol';
import { Icon, Style, Stroke, Fill, Text } from 'ol/style';
import VectorSource from 'ol/source/Vector';
import { LineString, Point } from 'ol/geom';
import IconAnchorUnits from 'ol/style/IconAnchorUnits';
import { Coordinate } from 'ol/coordinate';
import { boundingExtent } from 'ol/extent';
import { Color } from 'ol/color';
import ExtremeMapAbstractLayerManager, {
  DirectionArrowStyleParams,
  LayerWithInfo,
  DIRECTION_POINTER_INTERVAL,
  isAboveZoom1000M,
} from '@/lib/ExtremeMapAbstractLayerManager';
import { IconPath } from '@/models';
import { FeatureExt } from '@/lib/OlMapManager';
import { NamedVectorLayer } from '@/lib/OlMapWrapper';
import ExtremeMap from '@/components/lib/ExtremeMap/index.vue';
import type { ExternalReportSettings } from '@/models/apis/externalReports/external_report_settings';
import type { ExternalReportExt, ExternalReportPhoto } from '@/models/externalReports/externalReport';
import type { ExternalMtxExt } from '@/models/externalReports/externalMtx';
import { PHOTO_TYPE_WORK_PROGRESS, PHOTO_TYPE_ABNORMAL } from '@/consts/externalReports/standard';

interface Opts {
  photoTypeVisibility: Record<string, boolean>;
  externalReportUserSettings: ExternalReportSettings | null;
  showTrack: boolean;
}

interface CreateMtxPointParams {
  mtx: ExternalMtxExt;
  radiusLte: number;
  radiusGt: number;
  text: string;
  zIndex: number;
  rotation: number;
}

const PHOTO_PIN_TEXT_COLOR_MAP: Record<string, string> = {
  [PHOTO_TYPE_ABNORMAL]: '#000000',
  [PHOTO_TYPE_WORK_PROGRESS]: '#FFFFFF',
};

const MOVING_LINE_COLOR = 'rgba(127, 127, 127, 0.8)';
const WORKING_LINE_COLOR = 'rgba(56, 241, 215, 0.8)';
const ZOOM_LEVEL_LTE_1000M_LINE_WIDTH = 4;
const ZOOM_LEVEL_GT_1000M_LINE_WIDTH = 7;
const MOVING_MTX_POINT_FILL_COLOR: Color = [157, 157, 157, 1.0];
const MOVING_MTX_POINT_STROKE_COLOR: Color = [101, 101, 101, 1.0];
const WORKING_MTX_POINT_FILL_COLOR: Color = [56, 241, 215, 1.0];
const WORKING_MTX_POINT_STROKE_COLOR: Color = [37, 161, 144, 1.0];
const ZOOM_LEVEL_LTE_1000M_MTX_POINT_RADIUS = 2.4;
const ZOOM_LEVEL_GT_1000M_MTX_POINT_RADIUS = 3.6;

export default class ExtremeMapExternalReportLayerManager extends ExtremeMapAbstractLayerManager {
  layerName: string;
  opts: Opts;
  extremeMap: InstanceType<typeof ExtremeMap> | null;
  externalReport: ExternalReportExt | null;
  constructor(opts: Opts) {
    super();
    this.layerName = 'standard-reports';
    this.layerInfo.onLayerClick = () => {};
    this.opts = opts;
    this.extremeMap = null;
    this.externalReport = null;
  }

  getIconPath_(photo: ExternalReportPhoto): IconPath {
    if (!this.opts.externalReportUserSettings) {
      return {
        iconPath: '',
        selectedFramePath: '',
      };
    }
    const externalReportType = this.opts.externalReportUserSettings.external_report_type;
    const iconPath = `/static/img/externalReports/${externalReportType}/report_photo_${photo.photoType}_pin.png`;
    const selectedFramePath = '/static/img/pin_selected.png';
    return { iconPath, selectedFramePath };
  }

  getResourceStyles_(photos: ExternalReportPhoto[], zIndex: number): Style[] {
    const ret: Style[] = [];
    const firstPhoto = photos[0];
    const { iconPath, selectedFramePath } = this.getIconPath_(firstPhoto);
    if (firstPhoto.isSelected) {
      ret.push(new Style({
        image: new Icon({
          src: selectedFramePath,
          anchor: [0.5, 0.82],
          anchorXUnits: IconAnchorUnits.FRACTION,
          anchorYUnits: IconAnchorUnits.FRACTION,
          scale: 0.30,
          opacity: 1.0,
        }),
        zIndex: zIndex,
      }));
    }
    const textFontSize = photos.length > 1 ? 'bold 15px sans-serif' : 'bold 16px sans-serif';
    const textOffsetX = photos.length > 1 ? -2 : 0;
    const textColor = PHOTO_PIN_TEXT_COLOR_MAP[firstPhoto.photoType] ?? '#000000';
    ret.push(new Style({
      image: new Icon({
        src: iconPath,
        anchor: [0.5, 0.82],
        anchorXUnits: IconAnchorUnits.FRACTION,
        anchorYUnits: IconAnchorUnits.FRACTION,
        scale: 0.30,
      }),
      text: new Text({
        text: firstPhoto.dispOrder.toString(),
        font: textFontSize,
        fill: new Fill({ color: textColor }),
        offsetX: textOffsetX,
        offsetY: -23,
        rotation: 0,
      }),
      zIndex: zIndex,
    }));
    if (photos.length > 1) {
      ret.push(new Style({
        text: new Text({
          text: '+',
          font: 'bold 12px sans-serif',
          fill: new Fill({ color: textColor }),
          offsetX: 6,
          offsetY: -23,
          rotation: 0,
        }),
        zIndex: zIndex,
      }));
    }
    return ret;
  }

  createLineFeature_(points: Coordinate[], zIndex: number, status: string): Feature | null {
    // need at least 2 points to make a line.
    if (points.length < 2) {
      return null;
    }
    const line = new Feature({
      geometry: new LineString(points),
    });
    if (!this.opts.externalReportUserSettings) {
      return line;
    }
    const statusChoices = this.opts.externalReportUserSettings.choices.status;
    const lineColor = status === statusChoices.running.key ? MOVING_LINE_COLOR : WORKING_LINE_COLOR;
    // ズーム変化のたびに Feature を作り直さず、Style だけを resolution で切り替える
    const styleLte = new Style({
      stroke: new Stroke({ color: lineColor, width: ZOOM_LEVEL_LTE_1000M_LINE_WIDTH }),
      zIndex,
    });
    const styleGt = new Style({
      stroke: new Stroke({ color: lineColor, width: ZOOM_LEVEL_GT_1000M_LINE_WIDTH }),
      zIndex,
    });
    line.setStyle((_feat, resolution) => isAboveZoom1000M(resolution) ? styleGt : styleLte);
    (line as FeatureExt).onClickFunc = () => {};
    return line;
  }

  createCarLineFeatures_(mtxs: ExternalMtxExt[], zIndexStart: number):
    { lineFeats: Feature[]; lineZIndex: number; posCoords: Coordinate[] } {
    const lineFeats: Feature[] = [];
    const posCoords: Coordinate[] = [];
    let lineZIndex = zIndexStart;
    let movingPoints: Coordinate[] = [];
    let standardPoints: Coordinate[] = [];
    if (!this.opts.externalReportUserSettings) {
      return { lineFeats, lineZIndex, posCoords };
    }
    const statusChoices = this.opts.externalReportUserSettings.choices.status;
    let currentStatus = statusChoices.running.key;
    const len = mtxs.length;
    mtxs.forEach((mtx, i) => {
      const point = this.convCoord(mtx);
      if (mtx.status === statusChoices.running.key) {
        movingPoints.push(point);
      }
      if (mtx.status === statusChoices.working.key) {
        standardPoints.push(point);
      }
      posCoords.push(point);
      if (i === 0) {
        currentStatus = mtx.status;
        return;
      }
      if (currentStatus !== mtx.status || i === len - 1) {
        if (currentStatus === statusChoices.running.key) {
          movingPoints.push(point);
          const lineFeat = this.createLineFeature_(movingPoints, lineZIndex++, currentStatus);
          if (lineFeat) {
            lineFeats.push(lineFeat);
          }
          movingPoints = [];
        }
        if (currentStatus === statusChoices.working.key) {
          standardPoints.push(point);
          const lineFeat = this.createLineFeature_(standardPoints, lineZIndex++, currentStatus);
          if (lineFeat) { lineFeats.push(lineFeat); }
          standardPoints = [];
        }
        currentStatus = mtx.status;
      }
    });
    return { lineFeats, lineZIndex, posCoords };
  }

  createMtxPointFeatures_(
    mtxs: ExternalMtxExt[],
    zIndexStart: number,
  ): { mtxPointFeats: Feature[]; mtxPointZIndex: number } {
    const mtxPointFeats: Feature[] = [];
    let mtxPointZIndex = zIndexStart;
    const radiusLte = ZOOM_LEVEL_LTE_1000M_MTX_POINT_RADIUS;
    const radiusGt = ZOOM_LEVEL_GT_1000M_MTX_POINT_RADIUS;
    if (mtxs.length === 0) return { mtxPointFeats, mtxPointZIndex };

    // 最初と最後のpointをそれ以外のpointよりも上に表示するために、最初と最後のpointは後に追加する
    mtxs.forEach((mtx, idx) => {
      const isFirst = idx === 0;
      const isLast = idx === mtxs.length - 1;
      if (isFirst || isLast) return;
      const nextMtx = mtxs[idx + 1];
      const rotation = this.getBearing(mtx, nextMtx);
      const text = '';
      const showDirectionIcon = idx % DIRECTION_POINTER_INTERVAL === 0;
      const mtxPointParams: CreateMtxPointParams = {
        mtx,
        radiusLte,
        radiusGt,
        text,
        zIndex: mtxPointZIndex++,
        rotation,
      };
      const feat = showDirectionIcon
        ? this.createMtxDirectionArrowFeature_(mtxPointParams)
        : this.createMtxCirclePointFeature_(mtxPointParams);
      mtxPointFeats.push(feat);
    });
    if (mtxs.length === 1) {
      const onlyParams: CreateMtxPointParams = {
        mtx: mtxs[0],
        radiusLte: radiusLte * 2.0,
        radiusGt: radiusGt * 2.0,
        text: 'E',
        zIndex: mtxPointZIndex++,
        rotation: 0,
      };
      mtxPointFeats.push(this.createMtxCirclePointFeature_(onlyParams));
      return { mtxPointFeats, mtxPointZIndex };
    }
    const firstMtxPointParams: CreateMtxPointParams = {
      mtx: mtxs[0],
      radiusLte: radiusLte * 2.0,
      radiusGt: radiusGt * 2.0,
      text: 'S',
      zIndex: mtxPointZIndex++,
      rotation: 0,
    };
    const firstFeat = this.createMtxCirclePointFeature_(firstMtxPointParams);
    const lastMtxPointParams: CreateMtxPointParams = {
      mtx: mtxs[mtxs.length - 1],
      radiusLte: radiusLte * 2.0,
      radiusGt: radiusGt * 2.0,
      text: 'E',
      zIndex: mtxPointZIndex++,
      rotation: 0,
    };
    const lastFeat = this.createMtxCirclePointFeature_(lastMtxPointParams);
    mtxPointFeats.push(firstFeat, lastFeat);
    return { mtxPointFeats, mtxPointZIndex };
  }

  createMtxCirclePointFeature_({
    mtx,
    radiusLte,
    radiusGt,
    text,
    zIndex,
  }: CreateMtxPointParams): Feature {
    const feat = new Feature({
      geometry: new Point(this.convCoord(mtx)),
    });
    if (!this.opts.externalReportUserSettings) {
      return feat;
    }
    const statusChoices = this.opts.externalReportUserSettings.choices.status;
    const fillColor =
      mtx.status === statusChoices.running.key
        ? MOVING_MTX_POINT_FILL_COLOR
        : WORKING_MTX_POINT_FILL_COLOR;
    const strokeColor =
      mtx.status === statusChoices.running.key
        ? MOVING_MTX_POINT_STROKE_COLOR
        : WORKING_MTX_POINT_STROKE_COLOR;
    const baseParams = { fillColor, strokeColor, zIndex, text };
    const styleLte = text
      ? this.getCircleWithTextPointStyle({ ...baseParams, radius: radiusLte })
      : this.getCirclePointStyle({ ...baseParams, radius: radiusLte });
    const styleGt = text
      ? this.getCircleWithTextPointStyle({ ...baseParams, radius: radiusGt })
      : this.getCirclePointStyle({ ...baseParams, radius: radiusGt });
    feat.setStyle((_f, resolution) => isAboveZoom1000M(resolution) ? styleGt : styleLte);
    (feat as FeatureExt).onClickFunc = () => {
      if (this.extremeMap) {
        this.extremeMap.onClickExternalMtx(mtx);
      }
    };
    return feat;
  }

  createMtxDirectionArrowFeature_({
    mtx,
    zIndex,
    rotation,
  }: CreateMtxPointParams): Feature {
    const feat = new Feature({
      geometry: new Point(this.convCoord(mtx)),
    });
    const pointStyleParams: DirectionArrowStyleParams = {
      zIndex,
      rotation,
    };
    const featStyle = this.getDirectionArrowStyle(pointStyleParams);
    feat.setStyle(featStyle);
    (feat as FeatureExt).onClickFunc = () => {
      if (this.extremeMap) {
        this.extremeMap.onClickExternalMtx(mtx);
      }
    };
    return feat;
  }

  createPhotoFeatures_(
    photos: ExternalReportPhoto[],
    zIndexStart: number,
    photoTypeVisibility: Record<string, boolean>,
  ): { photoFeats: Feature[]; photoZIndex: number } {
    const photoFeats: Feature[] = [];
    let photoZIndex = zIndexStart;
    photos.forEach(photo => {
      if (photoTypeVisibility[photo.photoType] !== undefined && photoTypeVisibility[photo.photoType] === false) return;
      const samePosPhoto = (photo.samePosPhotos && photo.samePosPhotos.length > 0)
        ? [...photo.samePosPhotos]
          .filter(samePhoto => samePhoto.photoType === photo.photoType)
          .sort((a, b) => a.dispOrder - b.dispOrder)
        : [photo];
      if (photo.id !== samePosPhoto[0].id) { return; }
      const feat = new Feature({
        geometry: new Point(this.convCoord({
          lon: Number(photo.lon) || 0,
          lat: Number(photo.lat) || 0,
        })),
      });
      feat.setStyle(this.getResourceStyles_(samePosPhoto, photoZIndex++));
      (feat as FeatureExt).onClickFunc = () => {
        if (this.extremeMap) {
          this.extremeMap.onClickExternalReportPhoto(photo);
        }
      };
      photoFeats.push(feat);
    });

    return { photoFeats, photoZIndex };
  }

  createReportLayer_(
    reportExt: ExternalReportExt,
    { photoTypeVisibility, showTrack }: Opts,
  ): void {
    const features: Feature[] = [];
    let zIndex = 1;
    const reportPosCoords: Coordinate[] = [];

    // 車両移動の軌跡を描画する
    const { lineFeats, lineZIndex, posCoords } = this.createCarLineFeatures_(reportExt.mtxsExt, zIndex);
    // 軌跡を非表示にしても、地図のfit範囲は軌跡の座標で決める
    reportPosCoords.push(...posCoords);
    if (showTrack) {
      features.push(...lineFeats);
      zIndex = lineZIndex;

      // mtxsの点を描画する
      const { mtxPointFeats, mtxPointZIndex } = this.createMtxPointFeatures_(reportExt.mtxsExt, zIndex);
      features.push(...mtxPointFeats);
      zIndex = mtxPointZIndex;
    }

    // 写真のpinを描画する
    const { photoFeats } = this.createPhotoFeatures_(
      reportExt.photosExt,
      zIndex,
      photoTypeVisibility,
    );
    features.push(...photoFeats);
    const layer = new NamedVectorLayer('standard-reports', {
      source: new VectorSource({ features }),
    });
    const extent = reportPosCoords.length > 0 ? boundingExtent(reportPosCoords) : null;

    this.layer = layer;
    this.layerInfo.extent = extent;
    this.layerInfo.onLayerClick = ({ event, feature }) => {
      // 重なってたりする場合はそれぞれ飛んでくるので、一回で止める
      if (!event || event.originalEvent.defaultPrevented) { return; }
      event.preventDefault();
      if (feature) {
        (feature as FeatureExt).onClickFunc();
      }
    };
  }

  prepareLayer(
    extremeMap: InstanceType<typeof ExtremeMap>,
    reportExt: ExternalReportExt,
    opts: Opts,
  ): LayerWithInfo {
    this.extremeMap = extremeMap;
    this.opts = opts;
    this.externalReport = reportExt;
    this.createReportLayer_(reportExt, opts);
    return this.getLayer();
  }

  refreshLayerOnZoomChange(): void {
    // 各FeatureのStyleはresolution依存のStyleFunctionになっており、
    // OLがview変更時に自動で再評価するためレイヤー再構築は不要。
  }
}
